import React, { useState, useEffect, useRef } from 'react';
import {
  Bus, Car, ChevronRight, Camera, CheckCircle2,
  Fingerprint, Database, Zap, AlertTriangle,
  Image as ImageIcon, Navigation, Waypoints,
  ShieldCheck, Activity, UploadCloud, Microscope,
  FileCheck2, Scale,
} from 'lucide-react';

import AssetPericialService from '../../services/AssetPericialService';
import { AuditoriaNormativaService } from '../../services/AuditoriaNormativaService';
import { laudoApi } from '../../services/laudoApi';
import { ETAPA, FATORES_CONFIG, proximaEtapa, LIMITES } from '../../constants/vistoria';

const FATOR_ID = ETAPA.F02_INFRA;
const FATOR_CONFIG = FATORES_CONFIG[FATOR_ID];

const DICIONARIO_ABNT = {
  transporteColetivo: [
    'PLENO (ÔNIBUS/METRÔ/TREM < 500M)',
    'BOM (LINHAS REGULARES PRÓXIMAS)',
    'ADEQUADO (LINHAS EM VIAS COLETORAS)',
    'DEFICIENTE (BAIXA FREQUÊNCIA / DISTANTE)',
    'PRECÁRIO (INEXISTENTE NO ENTORNO IMEDIATO)',
  ],
  fluxoViario: [
    'VIA DE TRÁFEGO INTENSO / ARTERIAL',
    'VIA COLETORA / FLUXO MODERADO',
    'VIA LOCAL / ACESSO RESTRITO',
    'VIA DE PEDESTRES / CICLOVIA EXCLUSIVA',
    'VIA RURAL / FLUXO EVENTUAL',
  ],
  estacionamento: [
    'AMPLO (ZONA AZUL / PÁTIOS PÚBLICOS)',
    'PERMITIDO EM AMBOS OS LADOS',
    'RESTRITO (APENAS UM LADO / HORÁRIOS)',
    'PROIBIDO (VIA DE TRÂNSITO RÁPIDO)',
    'INEXISTENTE (VIA ESTREITA)',
  ],
  viasAcesso: [
    'ACESSOS PAVIMENTADOS E DUPLICADOS',
    'ACESSO PAVIMENTADO (PISTA ÚNICA)',
    'ACESSO EM SOLO NATURAL (TERRA)',
    'ACESSO BLOQUEADO / EM OBRAS',
  ],
};

const PADRAO_SISTEMA = {
  transporteColetivo: 'BOM (LINHAS REGULARES PRÓXIMAS)',
  fluxoViario: 'VIA LOCAL / ACESSO RESTRITO',
  estacionamento: 'PERMITIDO EM AMBOS OS LADOS',
  viasAcesso: 'ACESSO PAVIMENTADO (PISTA ÚNICA)',
};

const ITENS_UI = [
  { id: 'transporteColetivo', label: 'Transporte Público',  icon: Bus,      desc: 'Proximidade e diversidade de modais' },
  { id: 'fluxoViario',        label: 'Dinâmica Viária',     icon: Activity, desc: 'Hierarquia da via de acesso' },
  { id: 'estacionamento',     label: 'Estacionamento',      icon: Car,      desc: 'Facilidade de parada e guarda' },
  { id: 'viasAcesso',         label: 'Vias de Acesso',      icon: Waypoints,desc: 'Condição técnica de chegada' },
];

export default function VistoriaRegiaoFator02Infra({ setNavegacao, infoLaudo, peritoAtivo }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [activePhotoSlot, setActivePhotoSlot] = useState(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [loadingCaptura, setLoadingCaptura] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const [dadosTransporte, setDadosTransporte] = useState({
    ...PADRAO_SISTEMA,
    fatorValorizacao: '1.00',
    textoConsolidado: '',
    justificativaDivergencia: '',
    evidencias: {},
    justificativas: {},
  });

  const divergencias = Object.keys(PADRAO_SISTEMA).filter(
    k => dadosTransporte[k] !== PADRAO_SISTEMA[k]
  );

  const statusAuditoria = AuditoriaNormativaService.validarFinalizacao({
    ...dadosTransporte,
    justificativaResumo: dadosTransporte.justificativaDivergencia,
    _divergencias: divergencias,
  });

  useEffect(() => {
    const desvio = AuditoriaNormativaService.calcularDesvio(dadosTransporte.fatorValorizacao);
    const sinal = desvio >= 0 ? '+' : '';

    const base =
      `No tocante à acessibilidade regional, aplicou-se o **Fator de Transporte Urbano**, ` +
      `resultando em um ajuste de **${sinal}${desvio.toFixed(2)}%**. A zona de influência do ` +
      `imóvel apresenta sistema de transporte classificado como ${dadosTransporte.transporteColetivo}, ` +
      `operando em ${dadosTransporte.fluxoViario}. O cálculo observou a expressão normativa: ` +
      `$V_h = V_b \\times (1 + i)$.`;

    const adendo = dadosTransporte.justificativaDivergencia
      ? `\n\n**Justificativa de Mobilidade:** ${dadosTransporte.justificativaDivergencia}`
      : '';

    const fechamento =
      `\n\nFundamentação: **NBR 14653-2:2011, Item ${FATOR_CONFIG.nbrItem}**.`;

    setDadosTransporte(prev => ({
      ...prev,
      textoConsolidado: base + adendo + fechamento,
    }));
  }, [
    dadosTransporte.fatorValorizacao,
    dadosTransporte.justificativaDivergencia,
    dadosTransporte.transporteColetivo,
    dadosTransporte.fluxoViario,
  ]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activePhotoSlot) return;
    setErro(null);
    setLoadingCaptura(true);

    try {
      const resultado = await AssetPericialService.processarCaptura(
        file,
        FATOR_ID,
        infoLaudo?.numero || 'REPORT-F02',
        { perito: peritoAtivo, comodoLocal: activePhotoSlot, tentarGPS: true }
      );

      setDadosTransporte(prev => ({
        ...prev,
        evidencias: {
          ...prev.evidencias,
          [activePhotoSlot]: {
            url: resultado.url,
            file: resultado.url,       // compat
            hashIntegridade: resultado.hashIntegridade,
            fileName: resultado.fileName,
            metadados: resultado.metadados,
          },
        },
      }));
      setShowPhotoOptions(false);
    } catch (err) {
      setErro(err.message || 'Falha ao processar captura');
    } finally {
      setLoadingCaptura(false);
      e.target.value = null;
    }
  };

  const salvarEAvancar = async () => {
    if (statusAuditoria.isBloqueado) {
      setErro(`BLOQUEIO PERICIAL: ${statusAuditoria.mensagem}`);
      return;
    }
    setIsSaving(true);
    setErro(null);

    try {
      await laudoApi.salvarModulo({
        report_id: infoLaudo?.numero || 'LAUDO-F02',
        perito: peritoAtivo || 'MONTE_HERMOM_USER',
        fator: FATOR_ID,
        codigo: FATOR_CONFIG.codigo,
        pasta: FATOR_CONFIG.pasta,
        data_execucao: new Date().toISOString(),
        statusRevisao: 'PENDENTE',
        ...dadosTransporte,
      });
      setNavegacao(proximaEtapa(FATOR_ID));
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — dados salvos localmente.');
        setTimeout(() => setNavegacao(proximaEtapa(FATOR_ID)), 1200);
      } else {
        setErro(`Falha ao gravar: ${err.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isDivergente = (campo) => dadosTransporte[campo] !== PADRAO_SISTEMA[campo];

  return (
    <div className="max-w-5xl mx-auto space-y-12 italic font-black relative pb-32 uppercase">
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-[120px] z-0 select-none text-[#2F353B]">
        MONTE HERMOM
      </div>

      <input type="file" ref={cameraInputRef} onChange={onFileChange} accept="image/*" capture="environment" className="hidden" />
      <input type="file" ref={fileInputRef} onChange={onFileChange} accept="image/*" className="hidden" />

      <header className="flex justify-between items-end border-b-8 border-[#2F353B] pb-10 relative z-10">
        <div className="space-y-5">
          <div className="flex items-center gap-4 text-blue-600">
            <ShieldCheck size={24} className="animate-pulse" />
            <span className="text-[11px] tracking-[0.6em] text-slate-400 font-black uppercase">
              Auditoria de Mobilidade Urbana
            </span>
          </div>
          <h2 className="text-[22px] text-[#2F353B] tracking-[0.25em] leading-none uppercase">
            {FATOR_CONFIG.titulo.split('. ')[1]} <span className="text-blue-600">REGIONAL</span>
          </h2>
        </div>
        <div className="bg-[#2F353B] px-10 py-5 rounded-[2rem] border-b-4 border-blue-600 shadow-2xl text-white text-[15px] font-black italic">
          REPORT ID: {infoLaudo?.numero || '2026-F2'}
        </div>
      </header>

      {erro && (
        <div className="bg-red-900/90 text-white p-6 rounded-[2rem] flex items-center gap-6 relative z-10">
          <AlertTriangle size={24} />
          <span className="text-[11px] font-black italic tracking-widest">{erro}</span>
        </div>
      )}

      <section className="grid grid-cols-1 gap-8 relative z-10">
        {ITENS_UI.map((item) => {
          const divergente = isDivergente(item.id);
          const temFoto = !!dadosTransporte.evidencias[item.id];

          return (
            <div
              key={item.id}
              className={`p-1.5 rounded-[5.5rem] border-4 transition-all duration-500 ${
                divergente
                  ? 'bg-blue-50 border-blue-600 shadow-lg'
                  : 'bg-white border-slate-100 hover:border-blue-200'
              }`}
            >
              <div className="flex items-center gap-8 p-5">
                <div className="flex items-center gap-8 w-[40%] border-r-4 border-slate-50 mr-8">
                  <div className={`p-6 rounded-[3rem] ${divergente ? 'bg-blue-600 text-white' : 'bg-slate-50 text-blue-600'}`}>
                    <item.icon size={26} strokeWidth={2.5} />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[12px] tracking-widest font-black italic uppercase">{item.label}</label>
                    <span className="text-[8px] text-slate-400 tracking-tighter uppercase">{item.desc}</span>
                  </div>
                </div>

                <div className="flex-1">
                  <select
                    value={dadosTransporte[item.id]}
                    onChange={(e) => setDadosTransporte(prev => ({ ...prev, [item.id]: e.target.value }))}
                    className="w-full bg-transparent border-none text-slate-800 text-[13px] outline-none font-black italic cursor-pointer uppercase tracking-tighter"
                  >
                    {DICIONARIO_ABNT[item.id].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>

                <button
                  onClick={() => { setActivePhotoSlot(item.id); setShowPhotoOptions(true); }}
                  className={`ml-6 p-6 rounded-full border-4 transition-all ${
                    temFoto
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xl'
                      : 'bg-white border-slate-100 text-slate-200 hover:text-blue-600'
                  }`}
                >
                  {temFoto ? <CheckCircle2 size={24} /> : <Camera size={24} />}
                </button>
              </div>

              {divergente && !temFoto && (
                <div className="mt-6 ml-24 mb-4 animate-in slide-in-from-top-4">
                  <textarea
                    value={dadosTransporte.justificativas[item.id] || ''}
                    onChange={(e) => setDadosTransporte(prev => ({
                      ...prev,
                      justificativas: { ...prev.justificativas, [item.id]: e.target.value },
                    }))}
                    placeholder="Fundamentação técnica para ausência de evidência..."
                    className="w-full bg-white border-2 border-amber-200 rounded-[2.5rem] p-6 text-[11px] italic font-black text-slate-600 outline-none focus:border-amber-500"
                    rows={2}
                  />
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="bg-[#2F353B] p-16 rounded-[6rem] shadow-2xl border-b-[20px] border-blue-600 relative overflow-hidden z-10">
        <div className="relative z-10 space-y-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-[#1e293b] p-12 rounded-[4rem] text-center border border-white/5">
              <label className="text-[11px] text-blue-400 block mb-6 italic font-black uppercase tracking-[0.4em]">
                Fator de Transporte (i)
              </label>
              <div className="flex items-center justify-center gap-4">
                <Scale className="text-slate-500" size={24} />
                <input
                  type="text"
                  value={dadosTransporte.fatorValorizacao}
                  onChange={(e) => setDadosTransporte(prev => ({ ...prev, fatorValorizacao: e.target.value }))}
                  className="w-40 bg-transparent text-[32px] text-white italic outline-none text-center font-black border-b-4 border-blue-600"
                />
              </div>
            </div>
            <div className="bg-white/5 p-12 rounded-[4rem] flex flex-col items-center justify-center border border-white/10">
              <p className="text-white/40 text-[10px] mb-6 font-black italic tracking-[0.5em]">
                MÉTRICA NBR 14653
              </p>
              <p className="text-white text-[20px] font-black">$$V_h = V_b \times (1 + i)$$</p>
            </div>
          </div>

          <div className="bg-white/5 p-12 rounded-[5rem] border-2 border-white/5 space-y-8 backdrop-blur-md">
            <div className="flex items-center gap-6 border-b border-white/10 pb-8 text-white font-black italic">
              <Fingerprint className="text-blue-600" size={24} />
              <h3 className="text-[14px] tracking-[0.3em] uppercase">Texto Consolidado de Vistoria</h3>
            </div>
            <textarea
              readOnly
              value={dadosTransporte.textoConsolidado}
              className="w-full h-56 bg-transparent border-none text-slate-300 text-[12px] leading-[2.2] resize-none font-black italic outline-none text-justify"
            />
          </div>

          {divergencias.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 px-6 text-amber-500">
                <AlertTriangle size={20} />
                <span className="text-[10px] tracking-[0.3em] font-black uppercase italic">
                  Justificativa Técnica Obrigatória
                </span>
              </div>
              <textarea
                value={dadosTransporte.justificativaDivergencia}
                onChange={(e) => setDadosTransporte(prev => ({ ...prev, justificativaDivergencia: e.target.value }))}
                className="w-full h-32 p-8 rounded-[3.5rem] bg-white text-slate-800 text-[12px] italic font-black outline-none border-8 border-transparent focus:border-amber-500"
                placeholder="Informe as particularidades do transporte na região..."
              />
            </div>
          )}
        </div>
        <Database className="absolute -right-32 -bottom-32 text-white/[0.02] w-[500px] h-[500px] -rotate-12 pointer-events-none" />
      </section>

      <footer className="flex justify-between items-center pt-12 border-t-4 border-slate-100 relative z-10">
        <div className="flex flex-col font-black italic">
          <span className="text-[13px] text-[#2F353B] tracking-[0.6em] uppercase">MONTE HERMOM</span>
          <span className="text-[9px] text-blue-600 mt-3 font-black uppercase tracking-[0.3em]">
            Compliance Pericial 2026
          </span>
        </div>
        <button
          onClick={salvarEAvancar}
          disabled={isSaving}
          className="group flex items-center gap-14 bg-[#2F353B] text-white px-24 py-11 rounded-[5.5rem] shadow-2xl transition-all text-[18px] font-black italic hover:bg-blue-600 disabled:opacity-50"
        >
          {isSaving ? 'PROCESSANDO...' : 'SALVAR E IR PARA F3'}
          <ChevronRight size={28} className="group-hover:translate-x-6 transition-transform" />
        </button>
      </footer>

      {showPhotoOptions && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-[#2F353B]/95 backdrop-blur-xl p-10">
          <div className="bg-white w-full max-w-xl rounded-[6rem] p-16 space-y-12 border-[10px] border-blue-600 shadow-2xl text-center">
            <div className="bg-blue-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto text-blue-600">
              <Navigation size={40} />
            </div>
            <h3 className="text-[20px] font-black italic uppercase tracking-widest text-[#2F353B]">
              Admissão de Evidência Logística
            </h3>
            <div className="grid grid-cols-1 gap-6">
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-[#2F353B] text-white p-11 rounded-[3.5rem] flex items-center justify-center gap-6 font-black italic hover:bg-blue-600 disabled:opacity-50"
              >
                <Zap size={24} /> {loadingCaptura ? 'VALIDANDO...' : 'ATIVAR CÂMERA DE CAMPO'}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-slate-100 text-[#2F353B] p-11 rounded-[3.5rem] flex items-center justify-center gap-6 font-black italic border-4 border-slate-200 disabled:opacity-50"
              >
                <ImageIcon size={24} /> IMPORTAR DA GALERIA
              </button>
            </div>
            <button
              onClick={() => { setShowPhotoOptions(false); setActivePhotoSlot(null); }}
              className="w-full text-[11px] text-slate-300 font-black italic tracking-[0.6em] uppercase"
            >
              CANCELAR OPERAÇÃO
            </button>
          </div>
        </div>
      )}
    </div>
  );
}